import React, { useCallback, useEffect, useState } from "react";
import { Modal, Spin, Tooltip, Input, DatePicker, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import * as versionsApi from "../../../../core/data/redux/services/versionsApi";
import { fetchVersions } from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectCurrentProject,
  selectVersions,
} from "../../../../core/data/redux/projects/selectors";
import { useProjectCapability } from "../../hooks/useProjectRole";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";

import type { Version } from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

const VersionsSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const versions = useSelector(selectVersions);
  const activeCompany = useSelector(selectActiveCompany);
  const canManage = useProjectCapability(project?.id, "MANAGE_VERSION");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Version | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [releaseDate, setReleaseDate] = useState<dayjs.Dayjs | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      await dispatch(fetchVersions(project.id)).unwrap();
    } catch {
      /* silently handled */
    } finally {
      setLoading(false);
    }
  }, [dispatch, project?.id]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setStartDate(null);
    setReleaseDate(null);
    setEditorOpen(true);
  };

  const openEdit = (v: Version) => {
    setEditing(v);
    setName(v.name);
    setDescription(v.description || "");
    setStartDate(v.start_date ? dayjs(v.start_date) : null);
    setReleaseDate(v.release_date ? dayjs(v.release_date) : null);
    setEditorOpen(true);
  };

  const handleSubmit = async () => {
    if (!project || !name.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        name,
        description,
        start_date: startDate?.toISOString() || null,
        release_date: releaseDate?.toISOString() || null,
      };
      if (editing) {
        await versionsApi.updateVersion(activeCompany.company_id, activeCompany.company_type, editing.id, payload);
        notify.success("Version updated");
      } else {
        await versionsApi.createVersion(activeCompany.company_id, activeCompany.company_type, { project_id: project.id, ...payload });
        notify.success("Version created");
      }
      await reload();
      setEditorOpen(false);
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to save version"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRelease = async (v: Version) => {
    try {
      await versionsApi.releaseVersion(activeCompany.company_id, activeCompany.company_type, v.id);
      notify.success(`Version '${v.name}' released`);
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to release"));
    }
  };

  const handleArchive = async (v: Version) => {
    try {
      await versionsApi.archiveVersion(activeCompany.company_id, activeCompany.company_type, v.id);
      notify.success(`Version '${v.name}' archived`);
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to archive"));
    }
  };

  const handleUnarchive = async (v: Version) => {
    try {
      await versionsApi.unarchiveVersion(activeCompany.company_id, activeCompany.company_type, v.id);
      notify.success(`Version '${v.name}' unarchived`);
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to unarchive"));
    }
  };

  if (!project) {
    return (
      <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }} />
            <p className="mt-3 text-muted mb-0">Loading project…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
      <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-1 d-flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
            <i className="ti ti-package" style={{ color: "#3b82f6", fontSize: 20, flexShrink: 0 }} />
            <span>Versions</span>
          </h5>
          <span className="text-muted small">{versions.length} version{versions.length === 1 ? "" : "s"}</span>
        </div>
        {canManage && (
          <button type="button" className="btn btn-info btn-sm d-inline-flex align-items-center gap-1" style={{ borderRadius: 8, whiteSpace: "nowrap" }} onClick={openCreate}>
            <i className="ti ti-plus" style={{ fontSize: 16 }} />
            <span>New Version</span>
          </button>
        )}
      </div>
      <div className="card-body bg-white rounded-3">
        {loading && versions.length === 0 ? (
          <div className="text-center py-3"><Spin /></div>
        ) : versions.length === 0 ? (
          <div className="text-center py-4">
            <i className="ti ti-package" style={{ fontSize: 40, color: "#cbd5e1" }} />
            <p className="text-muted mt-2 mb-0">No versions yet. Create versions to track releases.</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {versions.map((v) => (
              <div
                key={v.id}
                className="d-flex align-items-center gap-3 px-3 py-2"
                style={{ borderRadius: 10, border: "1px solid #e2e8f0", transition: "all 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#93c5fd"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
              >
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: 32, height: 32, borderRadius: 8, background: v.released ? "#ecfdf5" : v.archived ? "#f1f5f9" : "#eff6ff", flexShrink: 0 }}
                >
                  <i className={`ti ${v.released ? "ti-circle-check" : v.archived ? "ti-archive" : "ti-package"}`} style={{ color: v.released ? "#10b981" : v.archived ? "#64748b" : "#3b82f6", fontSize: 16 }} />
                </div>
                <div className="flex-grow-1 min-width-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-semibold" style={{ fontSize: 13.5 }}>{v.name}</span>
                    {v.released && <Tag color="green" style={{ margin: 0, fontSize: 10, lineHeight: "18px", borderRadius: 10 }}>Released</Tag>}
                    {v.archived && <Tag color="default" style={{ margin: 0, fontSize: 10, lineHeight: "18px", borderRadius: 10 }}>Archived</Tag>}
                  </div>
                  {v.description && <div className="text-muted text-truncate" style={{ fontSize: 12 }}>{v.description}</div>}
                  <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: 11.5 }}>
                    {v.start_date && (
                      <span className="d-inline-flex align-items-center gap-1">
                        <i className="ti ti-calendar" style={{ fontSize: 12 }} />
                        {dayjs(v.start_date).format("MMM D, YYYY")}
                      </span>
                    )}
                    {v.start_date && v.release_date && <span>→</span>}
                    {v.release_date && (
                      <span className="d-inline-flex align-items-center gap-1">
                        {!v.start_date && <i className="ti ti-calendar" style={{ fontSize: 12 }} />}
                        {dayjs(v.release_date).format("MMM D, YYYY")}
                      </span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                    {!v.released && (
                      <Tooltip title="Release">
                        <button title="Release" type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => handleRelease(v)}>
                          <i className="ti ti-rocket" style={{ color: "#10b981", fontSize: 15 }} />
                        </button>
                      </Tooltip>
                    )}
                    {v.archived && (
                      <Tooltip title="Unarchive">
                        <button title="Unarchive" type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => handleUnarchive(v)}>
                          <i className="ti ti-archive-off" style={{ color: "#6b7280", fontSize: 15 }} />
                        </button>
                      </Tooltip>
                    )}
                    {!v.archived && (
                      <Tooltip title="Archive">
                        <button title="Archive" type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => handleArchive(v)}>
                          <i className="ti ti-archive" style={{ color: "#6b7280", fontSize: 15 }} />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <button title="Edit" type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => openEdit(v)}>
                        <i className="ti ti-edit" style={{ color: "#f59e0b", fontSize: 15 }} />
                      </button>
                    </Tooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        title={editing ? "Edit Version" : "New Version"}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText={editing ? "Update" : "Create"}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
        centered
        width="640px"
        zIndex={1001}
        styles={{
          content: { padding: 0, borderRadius: 12, overflow: "hidden" },
          header: { padding: "14px 20px 12px", borderBottom: "1px solid #f0f0f0", marginBottom: 0 },
          body: { padding: "16px 24px 20px" },
          footer: { padding: "10px 20px 14px", borderTop: "1px solid #f0f0f0", marginTop: 0 },
          mask: {
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            background: "rgba(0,0,0,0.45)",
          },
        }}
      >
        <div className="mb-3">
          <label className="form-label small fw-semibold">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. v1.2.0" />
        </div>
        <div className="mb-3">
          <label className="form-label small fw-semibold">Description</label>
          <Input.TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="row g-2">
          <div className="col-6">
            <label className="form-label small fw-semibold">Start Date</label>
            <DatePicker className="w-100" value={startDate} onChange={setStartDate} />
          </div>
          <div className="col-6">
            <label className="form-label small fw-semibold">Release Date</label>
            <DatePicker className="w-100" value={releaseDate} onChange={setReleaseDate} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VersionsSettings;
